'use client';

import FocusTrap from 'focus-trap-react';
import {
  ButtonHTMLAttributes,
  forwardRef,
  Fragment,
  ReactNode,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useDetectClickOutside } from '@/helpers/hooks';
import useDetectEscape from '@/helpers/hooks/useDetectEscape';
import cn from '@/helpers/utils/classNames';

import '@/styles/globals.v2.css';

import { Animate } from 'react-animate-mount';

import XClose from '../svg/XCloseRound';

const placements = {
  'top-left': 'v2-justify-start v2-items-start',
  'top-center': 'v2-justify-center v2-items-start',
  'top-right': 'v2-justify-end v2-items-start',
  'center-left': 'v2-justify-start v2-items-center',
  center: 'v2-justify-center v2-items-center',
  'center-right': 'v2-justify-end v2-items-center',
  'bottom-left': 'v2-justify-start v2-items-end',
  'bottom-center': 'v2-justify-center v2-items-end',
  'bottom-right': 'v2-justify-end v2-items-end',
};

const slideTopIn = 'slide-in-from-top';
const slideTopOut = 'slide-out-to-top';

const slideLeftIn = 'slide-in-from-left';
const slideLeftOut = 'slide-out-to-left';

const slideRightIn = 'slide-in-from-right';
const slideRightOut = 'slide-out-to-right';

const slideBottomIn = 'slide-in-from-bottom';
const slideBottomOut = 'slide-out-to-bottom';

const animations = {
  'top-left': {
    in: slideTopIn,
    out: slideTopOut,
  },
  'top-center': {
    in: slideTopIn,
    out: slideTopOut,
  },
  'top-right': {
    in: slideTopIn,
    out: slideTopOut,
  },
  'center-left': {
    in: slideLeftIn,
    out: slideLeftOut,
  },
  center: {
    in: slideTopIn,
    out: slideTopOut,
  },
  'center-right': {
    in: slideRightIn,
    out: slideRightOut,
  },
  'bottom-left': {
    in: slideBottomIn,
    out: slideBottomOut,
  },
  'bottom-center': {
    in: slideBottomIn,
    out: slideBottomOut,
  },
  'bottom-right': {
    in: slideBottomIn,
    out: slideBottomOut,
  },
};
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  closeBtnClassName?: string;
  containerClassName?: string;
  closeOnClickAway?: boolean;
  title?: ReactNode | string;
  titleWrapperClassName?: string;
  closeIconBtnClassName?: string;
  animateContent?: boolean;
  closeButton?: React.JSXElementConstructor<ButtonHTMLAttributes<HTMLButtonElement>>;
  disableCloseOnEscapeKeyPress?: boolean;
  animationType?: 'zoom' | 'slide';
  placement?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'center-left'
    | 'center'
    | 'center-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
  closeBtnContainerClassName?: string;
  zIndex?: `${number}` | number;
  isAlwaysOpenButInvisible?: boolean;
}

export type ModalRef = { close: () => void };

const Trap = ({ enabled = true, children }: { enabled?: boolean; children: ReactNode }) => {
  if (!enabled) return <Fragment>{children}</Fragment>;

  return <FocusTrap focusTrapOptions={{ allowOutsideClick: true }}>{children}</FocusTrap>;
};

export default forwardRef<ModalRef, ModalProps>(function Modal(props: ModalProps, ref) {
  const {
    children,
    isOpen,
    className,
    onClose,
    closeBtnClassName,
    containerClassName,
    placement = 'center',
    closeOnClickAway = false,
    animationType = 'slide',
    closeButton: CloseButton,
    animateContent = true,
    disableCloseOnEscapeKeyPress,
    title,
    titleWrapperClassName,
    closeIconBtnClassName,
    closeBtnContainerClassName,
    zIndex,
    isAlwaysOpenButInvisible = false,
  } = props;
  const [isCloseClicked, setCloseClicked] = useState(false);

  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  const closeTheModal = () => {
    setCloseClicked(true);
    const timeoutId = setTimeout(() => {
      onClose();
      setCloseClicked(false);
      clearTimeout(timeoutId);
    }, 200);
  };

  useImperativeHandle(ref, () => ({
    close: () => closeTheModal(),
  }));

  useDetectClickOutside(() => {
    if (closeOnClickAway) closeTheModal();
  }, modalRef);

  useDetectEscape(() => isOpen && !disableCloseOnEscapeKeyPress && closeTheModal(), modalRef);

  const animation = useMemo(() => {
    switch (animationType) {
      case 'zoom':
        return isCloseClicked ? 'animate-out zoom-out' : 'animate-in out zoom-in';
      case 'slide':
      default:
        return isCloseClicked ? `animate-out ${animations[placement].out}` : `animate-in ${animations[placement].in}`;
    }
  }, [isCloseClicked, placement, animationType]);

  if (!isOpen && !isAlwaysOpenButInvisible) return <></>;

  console.log('modal is content visible');

  const zIndexClassName = zIndex ? `v2-z-[${zIndex}]` : 'v2-z-50';

  return (
    <Trap enabled={!isAlwaysOpenButInvisible}>
      <div
        className={cn('modal-active v2-relative ', zIndexClassName, {
          'v2-invisible': !isOpen && isAlwaysOpenButInvisible,
        })}
        tabIndex={10000}
      >
        <div
          className={cn(
            'v2-fixed v2-top-0 v2-left-0 v2-h-full v2-w-full v2-bg-black v2-bg-opacity-40 v2-py-5 sm:v2-px-[15px] lg:v2-px-10 ',
            containerClassName
          )}
        >
          <div
            className={cn('v2-relative v2-flex v2-h-full v2-w-full !v2-duration-300', placements[placement], animation)}
          >
            <div
              ref={modalRef}
              className={cn('v2-relative v2-rounded-lg v2-bg-white v2-p-5 v2-opacity-100 v2-shadow-2xl', className)}
            >
              <div
                className={cn('v2-relative v2-mb-3 v2-flex v2-items-center v2-justify-between', titleWrapperClassName)}
              >
                <div className={cn('absolute top-1 right-1', closeBtnContainerClassName)}>
                  {CloseButton ? (
                    <CloseButton className={closeBtnClassName} onClick={closeTheModal} />
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={e => e.preventDefault()}
                        className="v2-absolute v2-right-0 v2-h-0 v2-w-0 v2-opacity-0 -v2-z-10"
                      />
                      <XClose
                        stroke="#325E4D"
                        onClick={e => {
                          e.preventDefault();
                          closeTheModal();
                        }}
                        className={cn('cursor-pointer', closeIconBtnClassName)}
                      />
                    </>
                  )}
                </div>

                {title && (
                  <h3 className="v2-font-sweet-sans-pro v2-font-normal v2-text-[#101828] v2-leading-8 text-[24px]">
                    {title}
                  </h3>
                )}
              </div>
              {/* {CloseButton ? (
                <CloseButton className={closeBtnClassName} onClick={closeTheModal} />
              ) : (
                <Button
                  ref={closeBtnRef}
                  type="button"
                  scheme="primary"
                  variant="filled"
                  className={cn(
                    'absolute -right-2 -top-2 scale-150 border-2 border-transparent p-1 outline-none outline-0 hover:bg-primary hover:text-white',
                    closeBtnClassName
                  )}
                  onClick={closeTheModal}
                >
                  <CloseOutlined />
                </Button>
              )} */}
              {animateContent ? <Animate show={isOpen}>{children}</Animate> : children}
            </div>
          </div>
        </div>
      </div>
    </Trap>
  );
});
